import * as React from 'react';
import { FilterProviderBase, FilterEditProps, isGenericEmptyValue, QueryStringTarget } from '.';
import MultipleTextFilterEdit from '../editors/MultipleTextFilterEdit';
import { RangedNumberFilterEdit, RangedDecimalFilterEdit, RangedIntegerFilterEdit } from '../editors';
import { Filter } from '..';
import { RangedValueDelimiter } from './Constants';
import { RangedValue } from './Types';

export interface RangedValueFilterEditProps extends FilterEditProps<RangedValue> {}

/**
 * Default RangedValue field provider, which assumes that the fromValue will be lower than toValue.
 *
 * @export
 * @class RangedValueFieldProvider
 * @implements {FieldProviderBase<RangedValue>}
 */
export class RangedValueFieldProvider implements FilterProviderBase<RangedValue> {
	public getComponent = (): any => MultipleTextFilterEdit;
	// eslint-disable-next-line no-unused-vars
	public getComponentProps = (filter: Filter) => ({});
	public getFriendlyValueForDisplay = (value: RangedValue): React.ReactNode => {
		if (!value) {
			return null;
		}

		if (value.fromValue && value.toValue) {
			return `Between ${value.fromValue} and ${value.toValue}`;
		} else if (value.fromValue) {
			return `More than ${value.fromValue}`;
		} else if (value.toValue) {
			return `Less than ${value.toValue}`;
		}

		return '<Empty>';
	};
	public getValueFromQueryString = (query: string): RangedValue | null => {
		if (!query || !query.trim()) {
			return { fromValue: null, toValue: null };
		}

		const values = query.split(RangedValueDelimiter);

		if (values.length === 2) {
			return { fromValue: values[0], toValue: values[1] };
		} else {
			return { fromValue: query, toValue: null };
		}
	};
	// eslint-disable-next-line no-unused-vars
	public getQueryStringFromValue = (value: RangedValue, filter: Filter, target: QueryStringTarget): string => {
		if (!value) {
			return '';
		}

		const fromValue = !isGenericEmptyValue(value.fromValue) ? value.fromValue : '';
		const toValue = !isGenericEmptyValue(value.toValue) ? value.toValue : '';

		return `${fromValue}${RangedValueDelimiter}${toValue}`;
	};
	public isEmptyValue = (value: RangedValue): boolean => {
		if (isGenericEmptyValue(value)) {
			return true;
		}

		if (isGenericEmptyValue(value.fromValue) && isGenericEmptyValue(value.toValue)) {
			return true;
		}

		return false;
	};
}

/**
 * Field provider for ranged numbers. Inherits the default implementation and only specifies a custom component.
 *
 * @export
 * @class RangedNumberFieldProvider
 * @extends {RangedValueFieldProvider}
 */
export class RangedNumberFieldProvider extends RangedValueFieldProvider {
	public getComponent = (): any => RangedNumberFilterEdit;
}

/**
 * Field provider for ranged decimals. Inherits the default implementation and only specifies a custom component.
 *
 * @export
 * @class RangedDecimalFieldProvider
 * @extends {RangedValueFieldProvider}
 */
export class RangedDecimalFilterProvider extends RangedValueFieldProvider {
	public getComponent = (): any => RangedDecimalFilterEdit;
}

/**
 * Field provider for ranged integer. Inherits the default implementation and only specifies a custom component.
 *
 * @export
 * @class RangedIntegerFieldProvider
 * @extends {RangedValueFieldProvider}
 */
export class RangedIntegerFilterProvider extends RangedValueFieldProvider {
	public getComponent = (): any => RangedIntegerFilterEdit;
}
